import { useTranslation } from "react-i18next";
import React, { useState } from "react";
import { Layout } from "@/components/layout";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft } from "lucide-react";
import { useLocation } from "wouter";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { StateSelect } from "@/components/ui/state-select";
import { MaskedInput } from "@/components/ui/masked-input";
import { PhoneField } from "@/components/PhoneField";
import { z } from "zod";
import useOrg from "@/hooks/use-org";
import { useUser } from "@clerk/clerk-react";

// Schema for org metadata form (name is managed by Clerk)
const orgMetadataSchema = z.object({
  hajocaCustomerId: z.string().optional(),
  phone: z.string().optional(),
  address: z
    .object({
      street: z.string().optional(),
      street2: z.string().optional(),
      city: z.string().optional(),
      state: z.string().optional(),
      zipCode: z.string().optional(),
      country: z.string().default("US"),
    })
    .optional(),
});

type OrgFormData = z.infer<typeof orgMetadataSchema>;

export default function CompanyProfilePage() {
  const { t } = useTranslation();
  const [, setLocation] = useLocation();
  const [editOpen, setEditOpen] = useState(false);

  const { org: orgData, updateOrg, isUpdating } = useOrg();

  // Get current user from Clerk
  const {} = useUser();

  // Form setup with validation
  const form = useForm<OrgFormData>({
    resolver: zodResolver(orgMetadataSchema),
    defaultValues: {
      hajocaCustomerId: orgData?.hajocaCustomerId || "",
      phone: orgData?.phone || "",
      address: {
        street: orgData?.address?.street || "",
        street2: orgData?.address?.street2 || "",
        city: orgData?.address?.city || "",
        state: orgData?.address?.state || "",
        zipCode: orgData?.address?.zipCode || "",
        country: orgData?.address?.country || "US",
      },
    },
  });

  // Update form when org data loads
  React.useEffect(() => {
    if (orgData) {
      form.reset({
        hajocaCustomerId: orgData.hajocaCustomerId || "",
        phone: orgData.phone || "",
        address: {
          street: orgData.address?.street || "",
          street2: orgData.address?.street2 || "",
          city: orgData.address?.city || "",
          state: orgData.address?.state || "",
          zipCode: orgData.address?.zipCode || "",
          country: orgData.address?.country || "US",
        },
      });
    }
  }, [orgData, form]);

  const onSubmit = (data: OrgFormData) => {
    updateOrg(data);
    setEditOpen(false);
  };

  return (
    <Layout headerContent={
      <div className="flex items-center gap-2 text-sm">
        <span className="text-muted-foreground">{t("common.admin")}</span>
        <span className="text-muted-foreground">/</span>
        <span className="font-semibold">{t("pages.admin.companyProfile.companyProfile")}</span>
      </div>
    }>
      <div className="space-y-6">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">
              {t("pages.admin.companyProfile.companyProfile")}
            </h1>
            <p className="text-muted-foreground">
              {t("pages.admin.companyProfile.seeAndEditYour")}
            </p>
          </div>
          <Button variant="outline" onClick={() => setLocation("/admin")}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t("pages.admin.companyProfile.backToAdmin")}
          </Button>
        </div>

        {/* Organization Info */}
        <div className="p-4 border rounded space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold">{t("pages.admin.companyProfile.organizationInfo")}</h2>
            <Dialog open={editOpen} onOpenChange={setEditOpen}>
              <DialogTrigger asChild>
                <Button>{t("pages.admin.companyProfile.editInfo")}</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{t("pages.admin.companyProfile.editOrganizationInfo")}</DialogTitle>
                </DialogHeader>
                <form
                  onSubmit={form.handleSubmit(onSubmit)}
                  className="space-y-4"
                >
                  <div className="space-y-2">
                    <Label htmlFor="phone">{t("pages.admin.companyProfile.phone")}</Label>
                    <PhoneField
                      value={form.watch("phone") || ""}
                      onChange={(value) => form.setValue("phone", value)}
                    />
                    <p className="text-sm text-muted-foreground">
                      {t("pages.admin.companyProfile.thisIsOrganizationPhone")}
                    </p>
                    {form.formState.errors.phone && (
                      <p className="text-sm text-red-600">
                        {form.formState.errors.phone.message}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="address.street">{t("pages.admin.companyProfile.streetAddress")}</Label>
                    <Input
                      id="address.street"
                      {...form.register("address.street")}
                      placeholder={t("pages.admin.companyProfile.123MainSt")}
                    />
                    {form.formState.errors.address?.street && (
                      <p className="text-sm text-red-600">
                        {form.formState.errors.address.street.message}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="address.street2">
                      {t("pages.admin.companyProfile.streetAddress2Optional")}
                    </Label>
                    <Input
                      id="address.street2"
                      {...form.register("address.street2")}
                      placeholder={t("pages.admin.companyProfile.suite100")}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="address.city">{t("pages.admin.companyProfile.city")}</Label>
                      <Input
                        id="address.city"
                        {...form.register("address.city")}
                        placeholder={t("pages.admin.companyProfile.newYork")}
                      />
                      {form.formState.errors.address?.city && (
                        <p className="text-sm text-red-600">
                          {form.formState.errors.address.city.message}
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="address.state">{t("pages.admin.companyProfile.state")}</Label>
                      <StateSelect
                        value={form.watch("address.state")}
                        onValueChange={(value) =>
                          form.setValue("address.state", value)
                        }
                      />
                      {form.formState.errors.address?.state && (
                        <p className="text-sm text-red-600">
                          {form.formState.errors.address.state.message}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="address.zipCode">{t("pages.admin.companyProfile.zipCode")}</Label>
                    <MaskedInput
                      id="address.zipCode"
                      mask="zipcode"
                      {...form.register("address.zipCode")}
                      placeholder="12345"
                    />
                    {form.formState.errors.address?.zipCode && (
                      <p className="text-sm text-red-600">
                        {form.formState.errors.address.zipCode.message}
                      </p>
                    )}
                  </div>

                  <Button
                    type="submit"
                    className="w-full"
                    disabled={isUpdating}
                  >
                    {isUpdating ? t("common.saving") : t("pages.admin.companyProfile.saveChanges")}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
          <hr />
          <div>
            <h3 className="font-bold">{t("pages.admin.companyProfile.organization")}</h3>

            <div className="space-y-1 text-sm">
              <div>
                <span className="font-medium">{t("pages.admin.companyProfile.phone_2")}</span>{" "}
                {orgData?.phone || t("pages.admin.companyProfile.notSet")}
              </div>

              <div>
                <span className="font-medium">{t("pages.admin.companyProfile.address")}</span>
                {orgData?.address?.street ? (
                  <div className="mt-1 text-muted-foreground">
                    <div>{orgData.address.street}</div>
                    {orgData.address.street2 && (
                      <div>{orgData.address.street2}</div>
                    )}
                    {(orgData.address.city ||
                      orgData.address.state ||
                      orgData.address.zipCode) && (
                      <div>
                        {[
                          orgData.address.city,
                          orgData.address.state,
                          orgData.address.zipCode,
                        ]
                          .filter(Boolean)
                          .join(", ")}
                      </div>
                    )}
                  </div>
                ) : (
                  <span className="text-muted-foreground"> {t("pages.admin.companyProfile.notSet")}</span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
